import { useId, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import {
  useAddProjectGitlabRepo,
  useCreateGitlabRule,
  useDeleteGitlabRule,
  useGitlabEnvironments,
  useGitlabMrsWithoutKey,
  useGitlabRepoSearch,
  useGitlabRules,
  useMe,
  useProjectGitlabRepos,
  useRegisterGitlabHookAgain,
  useRemoveProjectGitlabRepo,
  useUpdateGitlabRule,
  useUpdateProject,
} from "@/api/hooks";
import type { GitlabHookStatus } from "@/api/types/GitlabHookStatus";
import type { GitlabRepoOption } from "@/api/types/GitlabRepoOption";
import type { GitlabRuleEvent } from "@/api/types/GitlabRuleEvent";
import type { GitlabRuleView } from "@/api/types/GitlabRuleView";
import type { Project } from "@/api/types/Project";
import type { ProjectGitlabRepo } from "@/api/types/ProjectGitlabRepo";
import { useStatuses } from "@/api/work";
import { ConfirmDialog, Dialog } from "@/components/Dialog";
import { Icon, type IconName } from "@/components/Icon";
import { useToast } from "@/components/Toast";
import { Button, Checkbox, Chip, EmptyState, Field, Input, LoadError, Notice, Select, Spinner } from "@/components/ui";
import { errorMessage } from "@/lib/errors";
import { fullDate, relativeTime } from "@/lib/format";
import { useDebounced } from "@/lib/useDebounced";

const EVENTS: GitlabRuleEvent[] = ["mr_opened", "mr_merged", "deployed"];

const HOOK: Record<GitlabHookStatus, { tone: "pass" | "fail" | "outline"; icon?: IconName }> = {
  active: { tone: "pass", icon: "check" },
  failing: { tone: "fail", icon: "alert" },
  disabled: { tone: "fail", icon: "blocked" },
  none: { tone: "outline" },
};

/** Which GitLab repos feed a project, and the rules that move its tasks (project managers). */
export function GitlabSection({ project }: { project: Project }) {
  const { t } = useTranslation();
  const repos = useProjectGitlabRepos(project.key);

  if (repos.isPending)
    return (
      <section className="card p-5">
        <Spinner />
      </section>
    );
  if (repos.isError)
    return (
      <section className="card">
        <LoadError onRetry={() => void repos.refetch()} />
      </section>
    );
  const { connected, repos: list } = repos.data;
  if (!connected && list.length === 0) return <NotConnected />;

  return (
    <>
      <section className="card flex flex-col gap-4 p-5">
        <ReposHeader project={project} connected={connected} repos={list} />
        <p className="m-0 text-sm text-muted">{t("gitlabProject.intro")}</p>
        {!connected && <Notice tone="blocked">{t("gitlabProject.disconnected")}</Notice>}
        {list.length === 0 ? (
          <p className="m-0 text-sm text-muted">{t("gitlabProject.empty")}</p>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
            {list.map((r) => (
              <li key={r.id}>
                <RepoRow project={project} repo={r} connected={connected} />
              </li>
            ))}
          </ul>
        )}
      </section>
      <RulesCard project={project} />
      <FixOnMergeCard project={project} />
      <WithoutKeyCard project={project} />
    </>
  );
}

/** No connection means no picker: only a platform manager can change that. */
function NotConnected() {
  const { t } = useTranslation();
  const me = useMe();
  return (
    <section className="card">
      <EmptyState
        icon="branch"
        title={t("gitlabProject.notConnectedTitle")}
        body={me.role === "manager" ? t("gitlabProject.notConnectedManager") : t("gitlabProject.notConnectedAsk")}
        action={
          me.role === "manager" ? (
            <Link
              to="/admin/gitlab"
              className="inline-flex h-[30px] items-center rounded-[var(--radius-ctl)] border border-border-strong bg-surface px-2.5 text-sm font-semibold text-text no-underline hover:bg-surface-2 hover:no-underline"
            >
              {t("gitlabProject.openAdmin")}
            </Link>
          ) : undefined
        }
      />
    </section>
  );
}

function ReposHeader({ project, connected, repos }: { project: Project; connected: boolean; repos: ProjectGitlabRepo[] }) {
  const { t } = useTranslation();
  const [picking, setPicking] = useState(false);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <h3 className="m-0 label-caps">{t("gitlabProject.reposTitle")}</h3>
      {connected && !project.archived && (
        <Button size="sm" icon="plus" onClick={() => setPicking(true)}>
          {t("gitlabProject.addRepo")}
        </Button>
      )}
      {picking && <RepoPicker project={project} added={repos} onClose={() => setPicking(false)} />}
    </div>
  );
}

function RepoRow({ project, repo: r, connected }: { project: Project; repo: ProjectGitlabRepo; connected: boolean }) {
  const { t } = useTranslation();
  const toast = useToast();
  const again = useRegisterGitlabHookAgain(project.key);
  const remove = useRemoveProjectGitlabRepo(project.key);
  const [confirm, setConfirm] = useState(false);
  const hook = HOOK[r.hook_status];

  const registerAgain = () =>
    again.mutate(r.id, {
      onSuccess: (res) => {
        if (res.hook_status === "active") toast.success(t("gitlabProject.hookRegistered"));
        else toast.error(res.hook_error ?? t(`gitlabProject.hook.${res.hook_status}`));
      },
      onError: (e) => toast.error(e),
    });

  return (
    <div className="flex flex-col gap-2.5 rounded-[var(--radius-ctl)] border border-border p-3.5">
      <div className="flex flex-wrap items-center gap-2.5">
        <span className="inline-flex size-8 items-center justify-center rounded-md bg-surface-2 text-text-2">
          <Icon name="branch" />
        </span>
        <span className="min-w-0 flex-1 basis-40">
          <span className="block truncate font-semibold">{r.name}</span>
          <span className="block font-mono text-xs break-all text-muted">{r.path}</span>
        </span>
        <Chip tone={hook.tone} icon={hook.icon}>
          {t(`gitlabProject.hook.${r.hook_status}`)}
        </Chip>
      </div>

      {r.hook_status === "disabled" && !r.hook_error && <p className="m-0 text-xs text-fail-fg">{t("gitlabProject.hookDisabledHint")}</p>}
      {r.hook_error && (r.hook_status === "failing" || r.hook_status === "disabled") && (
        <p className="m-0 text-xs break-words text-fail-fg">{t("gitlabProject.hookError", { error: r.hook_error })}</p>
      )}
      {r.last_sync_error && <p className="m-0 text-xs break-words text-fail-fg">{t("gitlabProject.syncFailing", { error: r.last_sync_error })}</p>}

      <div className="flex flex-wrap items-center gap-2">
        <span className="min-w-0 flex-1 basis-48 text-xs text-muted">
          {r.last_event_at ? (
            <span title={fullDate(r.last_event_at)}>{t("gitlabProject.lastEvent", { time: relativeTime(r.last_event_at) })}</span>
          ) : (
            t("gitlabProject.noEvents")
          )}
          {r.last_synced_at && !r.last_sync_error && (
            <span title={fullDate(r.last_synced_at)}> · {t("gitlabProject.lastSynced", { time: relativeTime(r.last_synced_at) })}</span>
          )}
        </span>
        {!project.archived && (
          <>
            {connected && r.hook_status !== "active" && (
              <Button size="sm" icon="stale" loading={again.isPending} onClick={registerAgain}>
                {t("gitlabProject.registerAgain")}
              </Button>
            )}
            <Button size="sm" variant="ghost" icon="trash" aria-label={t("gitlabProject.removeRepo", { name: r.name })} onClick={() => setConfirm(true)} />
          </>
        )}
      </div>

      <ConfirmDialog
        open={confirm}
        onClose={() => setConfirm(false)}
        title={t("gitlabProject.removeRepo", { name: r.name })}
        body={t("gitlabProject.removeRepoBody")}
        confirmLabel={t("common.remove")}
        danger
        loading={remove.isPending}
        onConfirm={() =>
          remove.mutate(r.id, {
            onSuccess: () => {
              setConfirm(false);
              toast.success(t("gitlabProject.repoRemoved"));
            },
            onError: (e) => toast.error(e),
          })
        }
      />
    </div>
  );
}

/** GitLab's repo search, already cut down by the server to what platform managers allow. */
function RepoPicker({ project, added, onClose }: { project: Project; added: ProjectGitlabRepo[]; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [q, setQ] = useState("");
  const query = useDebounced(q.trim());
  const results = useGitlabRepoSearch(project.key, query);
  const add = useAddProjectGitlabRepo(project.key);

  const pick = (option: GitlabRepoOption) =>
    add.mutate(
      { gitlab_project_id: option.gitlab_project_id },
      {
        onSuccess: () => {
          toast.success(t("gitlabProject.repoAdded", { name: option.name }));
          onClose();
        },
        onError: (e) => toast.error(e),
      },
    );

  return (
    <Dialog open onClose={onClose} title={t("gitlabProject.addRepo")} width={560} footer={<Button onClick={onClose}>{t("common.cancel")}</Button>}>
      <div className="flex flex-col gap-3">
        <Field label={t("gitlabProject.search")}>
          {(p) => <Input {...p} autoFocus value={q} placeholder={t("gitlabProject.searchPlaceholder")} onChange={(e) => setQ(e.target.value)} />}
        </Field>
        {results.isPending ? (
          <Spinner />
        ) : results.isError ? (
          <div className="flex flex-col items-start gap-2">
            <Notice tone="blocked">{errorMessage(t, results.error)}</Notice>
            <Button size="sm" onClick={() => void results.refetch()}>
              {t("common.retry")}
            </Button>
          </div>
        ) : results.data.length === 0 ? (
          <p className="m-0 text-sm text-muted">{t("gitlabProject.noResults")}</p>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
            {results.data.map((o) => {
              const isAdded = added.some((r) => r.gitlab_project_id === o.gitlab_project_id);
              return (
                <li key={o.gitlab_project_id} className="flex flex-wrap items-center gap-2 rounded-[var(--radius-ctl)] border border-border px-3 py-2">
                  <span className="min-w-0 flex-1 basis-40">
                    <span className="block truncate font-medium">{o.name}</span>
                    <span className="block font-mono text-xs break-all text-muted">{o.path}</span>
                  </span>
                  {isAdded ? (
                    <Chip tone="neutral" icon="check">
                      {t("gitlabProject.added")}
                    </Chip>
                  ) : (
                    <Button size="sm" icon="plus" disabled={add.isPending} onClick={() => pick(o)}>
                      {t("common.add")}
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Dialog>
  );
}

function RulesCard({ project }: { project: Project }) {
  const { t } = useTranslation();
  const rules = useGitlabRules(project.key);
  const environments = useGitlabEnvironments(project.key);
  const [editing, setEditing] = useState<GitlabRuleView | "new" | null>(null);
  const seen = environments.data ?? [];

  return (
    <section className="card flex flex-col gap-4 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="m-0 label-caps">{t("gitlabProject.rulesTitle")}</h3>
        {!project.archived && (
          <Button size="sm" icon="plus" onClick={() => setEditing("new")}>
            {t("gitlabProject.addRule")}
          </Button>
        )}
      </div>
      <p className="m-0 text-sm text-muted">{t("gitlabProject.rulesIntro")}</p>

      {rules.isPending ? (
        <Spinner />
      ) : rules.isError ? (
        <LoadError onRetry={() => void rules.refetch()} />
      ) : rules.data.length === 0 ? (
        <p className="m-0 text-sm text-muted">{t("gitlabProject.rulesEmpty")}</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
          {rules.data.map((r) => (
            <li key={r.id}>
              <RuleRow project={project} rule={r} seen={seen} loadedSeen={environments.isSuccess} onEdit={() => setEditing(r)} />
            </li>
          ))}
        </ul>
      )}

      {editing && <RuleDialog project={project} rule={editing === "new" ? null : editing} seen={seen} onClose={() => setEditing(null)} />}
    </section>
  );
}

/** Off by default (R25). The bug moves as the GitLab bot, and only under the rules in the hint. */
function FixOnMergeCard({ project }: { project: Project }) {
  const { t } = useTranslation();
  const toast = useToast();
  const update = useUpdateProject(project.key);
  const locked = update.isPending || project.archived;
  return (
    <section className="card flex flex-col gap-4 p-5">
      <h3 className="m-0 label-caps">{t("gitlabProject.bugsTitle")}</h3>
      <label className={locked ? "flex cursor-not-allowed items-start gap-2.5 opacity-60" : "flex cursor-pointer items-start gap-2.5"}>
        <input
          type="checkbox"
          role="switch"
          className="mt-0.5 size-4 accent-[var(--accent)]"
          checked={project.fix_bugs_on_merge}
          disabled={locked}
          onChange={(e) => update.mutate({ fix_bugs_on_merge: e.target.checked }, { onError: (err) => toast.error(err) })}
        />
        <span className="flex flex-col">
          <span className="text-sm font-medium">{t("gitlabProject.fixOnMerge")}</span>
          <span className="text-xs text-muted">{t("gitlabProject.fixOnMergeHint")}</span>
        </span>
      </label>
    </section>
  );
}

/** Merged work Mixla couldn't tie to anything (R34). Mixla never comments on these in GitLab. */
function WithoutKeyCard({ project }: { project: Project }) {
  const { t } = useTranslation();
  const list = useGitlabMrsWithoutKey(project.key);
  return (
    <section className="card flex flex-col gap-4 p-5">
      <h3 className="m-0 label-caps">{t("gitlabProject.withoutKeyTitle")}</h3>
      <p className="m-0 text-sm text-muted">{t("gitlabProject.withoutKeyIntro", { key: project.key })}</p>
      {list.isPending ? (
        <Spinner />
      ) : list.isError ? (
        <LoadError onRetry={() => void list.refetch()} />
      ) : list.data.length === 0 ? (
        <p className="m-0 text-sm text-muted">{t("gitlabProject.withoutKeyEmpty")}</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
          {list.data.map((m) => (
            <li key={m.url} className="flex flex-col gap-1 rounded-[var(--radius-ctl)] border border-border p-3.5">
              <a href={m.url} target="_blank" rel="noreferrer" className="inline-flex max-w-full items-start gap-1.5 font-semibold break-words">
                <span className="min-w-0">{m.title}</span>
                <Icon name="ext" size={13} className="mt-1" />
              </a>
              <span className="font-mono text-xs break-all text-muted">
                {m.repo} !{m.iid} · {m.source_branch}
              </span>
              <span className="text-xs text-muted" title={fullDate(m.merged_at)}>
                {relativeTime(m.merged_at)}
                {m.author_username && ` · @${m.author_username}`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function RuleRow({
  project,
  rule: r,
  seen,
  loadedSeen,
  onEdit,
}: {
  project: Project;
  rule: GitlabRuleView;
  seen: string[];
  loadedSeen: boolean;
  onEdit: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const update = useUpdateGitlabRule(project.key);
  const remove = useDeleteGitlabRule(project.key);
  const [confirm, setConfirm] = useState(false);
  const event = r.event === "deployed" ? t("gitlabProject.events.deployedTo", { environment: r.environment ?? "" }) : t(`gitlabProject.events.${r.event}`);
  const notSeen = r.event === "deployed" && loadedSeen && !!r.environment && !seen.includes(r.environment);
  const broken = r.disabled_reason === "target_status_deleted";

  return (
    <div className="flex flex-col gap-2.5 rounded-[var(--radius-ctl)] border border-border p-3.5">
      <div className="flex flex-wrap items-center gap-2.5">
        <span className="flex min-w-0 flex-1 basis-56 flex-wrap items-center gap-x-1.5 gap-y-1">
          <b className="break-words">{event}</b>
          <Icon name="right" size={13} className="text-muted" />
          {r.target_status_name ? <span>{r.target_status_name}</span> : <span className="text-muted">{t("gitlabProject.statusGone")}</span>}
        </span>
        {broken ? (
          <Chip tone="fail" icon="blocked">
            {t("gitlabProject.ruleDisabled")}
          </Chip>
        ) : (
          <Checkbox
            label={r.enabled ? t("gitlabProject.ruleOn") : t("gitlabProject.ruleOff")}
            checked={r.enabled}
            disabled={update.isPending || project.archived}
            onChange={(e) =>
              update.mutate(
                { id: r.id, body: { environment: null, target_status_id: null, enabled: e.target.checked } },
                { onSuccess: () => toast.success(t("gitlabProject.ruleSaved")), onError: (err) => toast.error(err) },
              )
            }
          />
        )}
      </div>
      {broken && <p className="m-0 text-xs text-fail-fg">{t("gitlabProject.ruleDisabledHint")}</p>}
      {(notSeen || !project.archived) && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="min-w-0 flex-1">
            {notSeen && (
              <span title={t("gitlabProject.notSeenHint")}>
                <Chip tone="outline">{t("gitlabProject.notSeen")}</Chip>
              </span>
            )}
          </span>
          {!project.archived && (
            <>
              <Button size="sm" icon="edit" onClick={onEdit}>
                {t("common.edit")}
              </Button>
              <Button size="sm" variant="ghost" icon="trash" aria-label={t("gitlabProject.removeRule")} onClick={() => setConfirm(true)} />
            </>
          )}
        </div>
      )}
      <ConfirmDialog
        open={confirm}
        onClose={() => setConfirm(false)}
        title={t("gitlabProject.removeRule")}
        body={t("gitlabProject.removeRuleBody", { rule: event })}
        confirmLabel={t("common.remove")}
        danger
        loading={remove.isPending}
        onConfirm={() =>
          remove.mutate(r.id, {
            onSuccess: () => {
              setConfirm(false);
              toast.success(t("gitlabProject.ruleRemoved"));
            },
            onError: (e) => toast.error(e),
          })
        }
      />
    </div>
  );
}

function RuleDialog({ project, rule, seen, onClose }: { project: Project; rule: GitlabRuleView | null; seen: string[]; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const statuses = useStatuses(project.key);
  const create = useCreateGitlabRule(project.key);
  const update = useUpdateGitlabRule(project.key);
  const listId = useId();
  const [event, setEvent] = useState<GitlabRuleEvent>(rule?.event ?? "mr_merged");
  const [environment, setEnvironment] = useState(rule?.environment ?? "");
  const [statusId, setStatusId] = useState(rule?.target_status_id ?? "");
  const [tried, setTried] = useState(false);

  const deployed = event === "deployed";
  const env = environment.trim();
  const valid = !!statusId && (!deployed || !!env);
  const busy = create.isPending || update.isPending;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (!valid) return;
    const done = {
      onSuccess: () => {
        toast.success(t(rule ? "gitlabProject.ruleSaved" : "gitlabProject.ruleCreated"));
        onClose();
      },
      onError: (err: unknown) => toast.error(err),
    };
    const environmentBody = deployed ? env : null;
    if (rule) update.mutate({ id: rule.id, body: { environment: environmentBody, target_status_id: statusId, enabled: null } }, done);
    else create.mutate({ event, environment: environmentBody, target_status_id: statusId, enabled: false }, done);
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={rule ? t("gitlabProject.editRule") : t("gitlabProject.addRule")}
      width={480}
      footer={
        <>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" type="submit" form="gitlab-rule-form" loading={busy}>
            {rule ? t("common.save") : t("gitlabProject.addRule")}
          </Button>
        </>
      }
    >
      <form id="gitlab-rule-form" onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Field label={t("gitlabProject.ruleEvent")} hint={rule ? t("gitlabProject.ruleEventFixed") : undefined}>
          {(p) => (
            <Select {...p} value={event} disabled={!!rule} onChange={(e) => setEvent(e.target.value as GitlabRuleEvent)}>
              {EVENTS.map((ev) => (
                <option key={ev} value={ev}>
                  {t(`gitlabProject.events.${ev}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {deployed && (
          <Field
            label={t("gitlabProject.ruleEnvironment")}
            error={tried && !env ? t("gitlabProject.required") : undefined}
            hint={env && !seen.includes(env) ? `${t("gitlabProject.notSeen")}: ${t("gitlabProject.notSeenHint")}` : t("gitlabProject.environmentHint")}
          >
            {(p) => (
              <>
                <Input {...p} list={listId} autoComplete="off" maxLength={255} value={environment} onChange={(e) => setEnvironment(e.target.value)} />
                <datalist id={listId}>
                  {seen.map((name) => (
                    <option key={name} value={name} />
                  ))}
                </datalist>
              </>
            )}
          </Field>
        )}
        <Field label={t("gitlabProject.ruleStatus")} error={tried && !statusId ? t("gitlabProject.required") : undefined}>
          {(p) => (
            <Select {...p} value={statusId} onChange={(e) => setStatusId(e.target.value)}>
              <option value="" disabled>
                {t("gitlabProject.pickStatus")}
              </option>
              {(statuses.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {!rule && <p className="m-0 text-xs text-muted">{t("gitlabProject.ruleStartsOff")}</p>}
      </form>
    </Dialog>
  );
}
